import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import {
  MediaApprovalMaType,
  GET_MEDIA_CATEGORY_AND_GENRE,
  GetMediaCategoryAndGenreData,
  GetMediaCategoryAndGenreVariables,
} from '@jwp/ott-common/src/graphql/query/moderate.queries';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import FilterMob from '@jwp/ott-theme/assets/icons/FilterMob.svg?react';
import { DateRangeType } from '@jwp/ott-common/src/stores/ModerateStore';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';

import Icon from '../Icon/Icon';
import Skeleton from '../Skeleton/Skeleton';
import Button from '../Button/Button';

import styles from './ModerateContentFilter.module.scss';

export type { DateRangeType } from '@jwp/ott-common/src/stores/ModerateStore';

interface Props {
  appliedDateRange: DateRangeType;
  appliedContentTypes: MediaApprovalMaType[];
  appliedCategories: string[];
  onApply: (dateRange: DateRangeType, contentTypes: MediaApprovalMaType[], categories: string[]) => void;
  className?: string;
}

const ModerateContentFilter: React.FC<Props> = ({ appliedDateRange, appliedContentTypes, appliedCategories, onApply, className }) => {
  const { t } = useTranslation(['upload']);
  const breakpoint = useBreakpoint();
  const isMobileView = breakpoint < Breakpoint.md;
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Filter States
  const [tempDateRange, setTempDateRange] = React.useState<DateRangeType>(appliedDateRange);
  const [tempContentTypes, setTempContentTypes] = React.useState<MediaApprovalMaType[]>(appliedContentTypes);
  const [tempCategories, setTempCategories] = React.useState<string[]>(appliedCategories);

  // Sync temp state with applied state when opening/closing or when applied state changes
  React.useEffect(() => {
    if (isFilterOpen) {
      setTempDateRange(appliedDateRange);
      setTempContentTypes(appliedContentTypes);
      setTempCategories(appliedCategories);
    }
  }, [isFilterOpen, appliedDateRange, appliedContentTypes, appliedCategories]);

  // Category Pagination State
  const categoryLimit = 12;

  const {
    data: categoryData,
    loading: categoryLoading,
    fetchMore: fetchMoreCategories,
  } = useQuery<GetMediaCategoryAndGenreData, GetMediaCategoryAndGenreVariables>(GET_MEDIA_CATEGORY_AND_GENRE, {
    variables: {
      first: categoryLimit,
      offset: 0,
    },
  });

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasChanges = React.useMemo(() => {
    const sortedTempTypes = [...tempContentTypes].sort((a, b) => a.localeCompare(b)).join(',');
    const sortedAppliedTypes = [...appliedContentTypes].sort((a, b) => a.localeCompare(b)).join(',');
    const sortedTempCats = [...tempCategories].sort((a, b) => a.localeCompare(b)).join(',');
    const sortedAppliedCats = [...appliedCategories].sort((a, b) => a.localeCompare(b)).join(',');

    return tempDateRange !== appliedDateRange || sortedTempTypes !== sortedAppliedTypes || sortedTempCats !== sortedAppliedCats;
  }, [tempDateRange, appliedDateRange, tempContentTypes, appliedContentTypes, tempCategories, appliedCategories]);

  const handleApplyFilters = () => {
    onApply(tempDateRange, tempContentTypes, tempCategories);
    setIsFilterOpen(false);
  };

  const handleClearAll = () => {
    setTempDateRange(null);
    setTempContentTypes([]);
    setTempCategories([]);
  };

  const handleCategoryToggle = (value: string) => {
    setTempCategories((prev) => (prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]));
  };

  const handleContentTypeToggle = (type: MediaApprovalMaType) => {
    setTempContentTypes((prev) => (prev.includes(type) ? [] : [type]));
  };

  const categoryNodes = categoryData?.getMediaCategoryAndGenre?.nodes || [];
  const totalCategories = categoryData?.getMediaCategoryAndGenre?.totalCount || 0;
  const hasMoreCategories = categoryNodes.length < totalCategories;

  const handleLoadMoreCategories = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom < 40 && hasMoreCategories && !categoryLoading) {
      fetchMoreCategories({
        variables: {
          offset: categoryNodes.length,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) { return prev; }
          return {
            getMediaCategoryAndGenre: {
              ...fetchMoreResult.getMediaCategoryAndGenre,
              nodes: [...prev.getMediaCategoryAndGenre.nodes, ...fetchMoreResult.getMediaCategoryAndGenre.nodes],
            },
          };
        },
      });
    }
  };

  return (
    <div className={classNames(styles.filterWrapper, className)} ref={dropdownRef}>
      {isMobileView ? (
        <button
          type="button"
          className={classNames(styles.filterIconBtn, { [styles.open]: isFilterOpen })}
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          aria-label={t('upload:moderate.filter')}
        >
          <FilterMob className={styles.filterIcon} aria-hidden="true" />
        </button>
      ) : (
        <button
          type="button"
          className={classNames(styles.filterBtn, { [styles.open]: isFilterOpen })}
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          <span>{t('upload:moderate.filter')}</span>
          <Icon icon={ChevronRight} className={styles.icon} />
        </button>
      )}
      {isFilterOpen && isMobileView && (
        <button
          type="button"
          className={styles.filterBackdrop}
          aria-label={t('upload:moderate.filter')}
          onClick={() => setIsFilterOpen(false)}
        />
      )}
      {isFilterOpen && (
        <div className={classNames(styles.filterPopup, { [styles.filterPopupCentered]: isMobileView })}>
          <div className={styles.popupHeader}>
            <h3>{t('upload:moderate.add_filters')}</h3>
            <button type="button" className={styles.clearBtn} onClick={handleClearAll}>
              {t('upload:moderate.clear_all')}
            </button>
          </div>

          <div className={styles.filterPopupBody} onScroll={handleLoadMoreCategories}>
            <div className={styles.filterSection}>
              <span className={styles.sectionLabel}>{t('upload:moderate.date_range')}</span>
              <div className={styles.chipGroup}>
                {(['today', 'lastWeek', 'lastMonth', 'oldest'] as DateRangeType[]).map((range) => {
                  const rangeLabels: Record<string, string> = {
                    today: t('upload:moderate.today'),
                    lastWeek: t('upload:moderate.last_week'),
                    lastMonth: t('upload:moderate.last_month'),
                    oldest: t('upload:moderate.oldest'),
                  };
                  return (
                    <button
                      key={range}
                      type="button"
                      className={classNames(styles.chip, { [styles.active]: tempDateRange === range })}
                      onClick={() => setTempDateRange((prev) => (prev === range ? null : range))}
                    >
                      {range ? rangeLabels[range] : ''}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.filterSection}>
              <span className={styles.sectionLabel}>{t('upload:moderate.content_type')}</span>
              <div className={styles.chipGroup}>
                {(['REELS', 'VIDEOS'] as MediaApprovalMaType[]).map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={classNames(styles.chip, { [styles.active]: tempContentTypes.includes(type) })}
                    onClick={() => handleContentTypeToggle(type)}
                  >
                    {type === 'REELS' ? t('upload:moderate.reel') : t('upload:moderate.video')}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.filterSection}>
              <span className={styles.sectionLabel}>{t('upload:moderate.category_genre')}</span>
              <div className={classNames(styles.chipGroup, styles.chipGroupWrap)}>
                {categoryNodes.map((cat) => (
                  <button
                    key={cat.ddValue}
                    type="button"
                    className={classNames(styles.chip, { [styles.active]: tempCategories.includes(cat.ddValue) })}
                    onClick={() => handleCategoryToggle(cat.ddValue)}
                  >
                    {cat.ddLabel}
                  </button>
                ))}
                {categoryLoading && <Skeleton width="80px" height="32px" borderRadius="16px" />}
              </div>
            </div>
          </div>

          <div className={styles.popupFooter}>
            <Button
              label={t('upload:moderate.apply_filter')}
              color="primary"
              variant="contained"
              fullWidth
              className={styles.applyBtn}
              onClick={handleApplyFilters}
              disabled={!hasChanges}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ModerateContentFilter;
